/**
 * Implements software requirements: H1SR-11
 *
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-11/Default-values-for-cases-can-be-defined-in-the-hip-preferences
 */
import { computed, reactive, ref, type Ref } from 'vue';
import type { AxiosRequestConfig } from 'axios';
import { defineStore } from 'pinia';
import { isEqual } from 'lodash';
import type { PreferredStem, UserPreferences } from '@/api/preferences';
import { getUserPreferences, saveUserPreferences, type THAPreferences } from '@/api/preferences';
import { isUserFriendlyErrorMessage } from '@/api/http';
import { isValid, validators } from '@/views/preferences/preferencesValidation';
import { DataState, makeDataState, withStateLoading } from '@/util/dataState';

export interface CupFormInputs {
    inclinationAngle: string;
    fitMethod: string;
    anteversion: string;
    anteversionAngle: string;
    alignment: string;
}

export interface FormInputs {
    preferredSystem: string;
    stems: string[];
    cup: CupFormInputs;
}

const emptyCupFormInputs = (): CupFormInputs => ({
    inclinationAngle: '',
    fitMethod: '',
    anteversion: '',
    anteversionAngle: '',
    alignment: '',
});

const emptyFormInputs = (): FormInputs => ({
    preferredSystem: '',
    stems: [],
    cup: emptyCupFormInputs(),
});

/** A comparable snapshot: reactive references dropped and stems put in a stable order. */
const snapshotOf = ({ preferredSystem, stems, cup }: FormInputs): FormInputs => ({
    preferredSystem,
    stems: [...stems].sort(),
    cup: { ...cup },
});

/** Overwrite the form in place with what the resource returned for this surgeon. */
function applyLoaded(state: FormInputs, { preferences }: UserPreferences): void {
    state.preferredSystem = preferences.preferredSystem;
    state.stems = [...preferences.stems];
    state.cup = {
        inclinationAngle: preferences.cupInclinationAngle ?? '',
        fitMethod: preferences.cupFitMethod ?? '',
        anteversion: preferences.cupAnteversionMode ?? '',
        anteversionAngle: preferences.cupAnteversionAngle ?? '',
        alignment: preferences.cupAlignMode ?? '',
    };
}

function fieldHasError(state: FormInputs, field: string): boolean {
    if (validators[field] === undefined) {
        throw new Error(`no validator for ${field}`);
    }
    return !validators[field](state);
}

const loadingFlags = (holder: { state: DataState }) => ({
    isLoading: computed(() => holder.state === DataState.Loading),
    isSaving: computed(() => holder.state === DataState.Saving),
    isReady: computed(() => holder.state !== DataState.Loading),
    loadFailed: computed(() => holder.state === DataState.Error),
});

/** The form, and the snapshot that decides whether it has been edited since it was loaded. */
function formState() {
    const state = reactive<FormInputs>(emptyFormInputs());
    const lastSaved: Ref<FormInputs> = ref<FormInputs>(emptyFormInputs());

    return {
        state,
        hasUnsavedChanges: computed(() => !isEqual(lastSaved.value, snapshotOf(state))),
        /** Take the form as it now stands to be the saved one. */
        markSaved: () => {
            lastSaved.value = snapshotOf(state);
        },
        clearForm: () => {
            Object.assign(state, emptyFormInputs());
            lastSaved.value = snapshotOf(state);
        },
    };
}

/** The outcome of the last save attempt, read by the ViewModel for UI presentation. */
function saveResult() {
    const saveError = ref<string | null>(null);
    const saveSuccess = ref(false);
    const validationFailed = ref(false);

    const clearSaveResult = () => {
        saveError.value = null;
        saveSuccess.value = false;
        validationFailed.value = false;
    };

    return { saveError, saveSuccess, validationFailed, clearSaveResult };
}

const toRequest = (state: FormInputs): THAPreferences => ({
    cupAlignMode: state.cup.alignment,
    cupAnteversionAngle: state.cup.anteversionAngle,
    cupAnteversionMode: state.cup.anteversion,
    cupFitMethod: state.cup.fitMethod,
    cupInclinationAngle: state.cup.inclinationAngle,
    stems: state.stems,
    preferredSystem: state.preferredSystem,
});

export const usePreferences = defineStore('preferences', () => {
    const options = ref<{ stems: PreferredStem[] }>({ stems: [] });
    const { state, hasUnsavedChanges, markSaved, clearForm } = formState();

    const _stateHolder = reactive(makeDataState());
    const { isLoading, isSaving, isReady, loadFailed } = loadingFlags(_stateHolder);

    const { saveError, saveSuccess, validationFailed, clearSaveResult } = saveResult();

    /**
     * Reset before loading a different surgeon, so a stale lastSavedState cannot make the new
     * surgeon's untouched form claim it has unsaved changes.
     */
    function _clearState() {
        options.value.stems = [];
        clearForm();
        clearSaveResult();
        Object.assign(_stateHolder, makeDataState());
    }

    async function load(userId: number, config?: AxiosRequestConfig) {
        _clearState();

        await withStateLoading(
            _stateHolder,
            async () => {
                const result = await getUserPreferences(userId, config);

                options.value.stems = [...result.options.stems];
                applyLoaded(state, result);
                markSaved();
            },
            config?.signal,
        );
    }

    async function save(userId: number) {
        clearSaveResult();
        _stateHolder.state = DataState.Saving;

        if (!isValid(state)) {
            validationFailed.value = true;
            _stateHolder.state = DataState.Idle;
            return;
        }

        const result = await saveUserPreferences(userId, toRequest(state), options.value);
        _stateHolder.state = DataState.Idle;

        if (isUserFriendlyErrorMessage(result)) {
            saveError.value = result;
        } else {
            markSaved();
            saveSuccess.value = true;
        }
    }

    return {
        state,
        options,
        isLoading,
        isSaving,
        isReady,
        loadFailed,
        $reset: _clearState,
        load,
        save,
        saveError,
        saveSuccess,
        validationFailed,
        hasError: (field: string) => fieldHasError(state, field),
        hasUnsavedChanges,
    };
});
